import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { PeriodPicker, periodoQS, periodoTexto } from '../components/PeriodPicker';
import { api } from '../lib/api';
import { useLive } from '../lib/useLive';
import { ROL_ETIQUETA } from '../lib/roles';
import { CarteraDetalle, bajarCartera } from '../components/CarteraDetalle';
import { avisar, textoDeError } from '../components/Dialogo';
import { Barras } from '../components/Barras';
import { dinero, entero, porcentaje, enPeriodo } from '../lib/graficas';
import { usePeriodoInicial } from '../lib/preferencias';

/**
 * EQUIPO en el periodo que se elija (0.39.0; asesoría desde Halory en 0.40.0). Cada persona del equipo con lo
 * suyo: asesoría (conversaciones y chats esperando en Halory, citas que agendó ella o la IA, y lo que compró su
 * cartera), equipo médico y cosmetología (lo que realizó) y recepción (lo que cobró). Se actualiza sola.
 *
 * 0.42.0 — Halory manda: quien atiende en Halory sale con sus números aunque no tenga cuenta en DTB HUB. Aparte sólo
 * queda la cartera que no atiende nadie (a nombre de alguien que ya no está en Halory, casi siempre del Excel del
 * CRM, o de nadie), con su detalle y "Bajar a Excel" para repartirla (CarteraDetalle). Cualquier tarjeta abre
 * también su cartera.
 */
interface Asesoria {
  asesora_id: string | null; user_id: string | null; nombre: string; sin_asignar: boolean; sin_cuenta: boolean;
  /** Atiende alguien: está hoy en el equipo de Halory (si no se sabe quién está, una cuenta de ventas activa en el HUB). */
  trabaja?: boolean; cuenta_hub?: boolean; en_halory?: boolean;
  cartera: { asignados: number; pacientes: number; leads: number; quejas: number };
  /** conversaciones / esperando / quejas_chat en null = no se pudo preguntar a Halory (ver `halory.motivo`). */
  periodo: { conversaciones: number | null; esperando: number | null; quejas_chat: number | null; escribieron: number;
             citas: number; citas_ia: number; asistieron: number; nuevos: number; nuevos_leads: number; ventas: number; tickets: number };
}
interface HaloryInfo {
  ok: boolean; motivo: string | null; truncado: boolean;
  sinCuenta: { nombre: string; conversaciones: number; esperando: number; leads: number }[];
  sync: { ultimo: string | null; motivo: string | null; reasignados: number | null; altas: number | null };
  equipo?: { fuente: 'halory' | 'reloj' | null; personas: number; at: string | null };
}
interface Clinico { user_id: string; nombre: string; rol: string | null; activo: boolean; procedimientos: number; cobrado: number; pacientes: number; farmacia: { lineas: number; cobrado: number } }
interface Recep { user_id: string; nombre: string; rol: string | null; activo: boolean; tickets: number; cobrado: number; canceladas: number; farmacia: { lineas: number; cobrado: number } }
interface Resp { periodo: { label: string; all: boolean }; asesoria: Asesoria[]; clinico: Clinico[]; recepcion: Recep[]; halory?: HaloryInfo }

const money = (n: number) => '$' + Math.round(n).toLocaleString('es-MX');
const CSS = `
.eq-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fill, minmax(min(100%, 290px), 1fr)); }
.eq-card { padding: 14px 16px; display: grid; gap: 10px; min-width: 0; }
.eq-cab { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.eq-cab b { font-size: 16px; color: var(--ink); }
.eq-kpis { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.eq-kpi { background: var(--bg-alt); border-radius: 12px; padding: 8px 9px; min-width: 0; container-type: inline-size; }
.eq-kpi span { display: block; font-size: 11px; color: var(--ink-soft); line-height: 1.2; }
/* Un monto no se parte a la mitad ("$376,16 / 0"): se achica en celular y sólo si de plano no cabe, se corta. */
.eq-kpi strong { display: block; font-size: 18px; font-weight: 600; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-variant-numeric: tabular-nums; }
@media (max-width: 480px) { .eq-kpi strong { font-size: 15.5px; } .eq-kpis { gap: 6px; } }
/* Y se achica a lo que mida SU cuadro (--n = cuántos caracteres): "$1,205,230" se lee entero en vez de "$1,20…".
   Un navegador sin unidades de contenedor ignora esta línea y se queda con la de arriba. */
.eq-kpi strong { font-size: min(18px, calc(100cqi / (var(--n, 4) * 0.72))); }
.eq-kpi.oro strong { color: var(--gold-dark); }
.eq-kpi.ambar strong { color: var(--warning); }
.eq-cartera { font-size: 12.5px; color: var(--ink-soft); line-height: 1.45; }
.eq-cartera b { color: var(--ink); font-weight: 600; }
.eq-h2 { margin: 22px 2px 8px; font-size: 17px; font-weight: 600; color: var(--gold-dark); }
.eq-nota { font-size: 12.5px; color: var(--ink-soft); margin: -2px 2px 10px; }
.eq-pill { font-size: 11px; font-weight: 600; padding: 2px 9px; border-radius: 999px; background: var(--warning-bg); color: var(--warning); white-space: nowrap; }
.eq-pill.gris { background: var(--bg-alt); color: var(--ink-soft); }
.eq-ver { background: none; border: none; box-shadow: none; padding: 0; font-size: 12px; color: var(--gold-dark); font-weight: 600; cursor: pointer; text-decoration: underline; text-underline-offset: 2px; }
.eq-ver:hover:not(:disabled) { transform: none; box-shadow: none; }
.eq-huerf { display: grid; gap: 8px; }
.eq-huerf-fila { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; font-size: 13px; padding: 6px 0; border-top: 1px solid var(--border); }
.eq-huerf-fila:first-child { border-top: none; }
.eq-huerf-fila .acc { margin-left: auto; display: flex; gap: 12px; }
`;

function K({ label, valor, tono }: { label: string; valor: string | number; tono?: 'oro' | 'ambar' }) {
  return <div className={`eq-kpi ${tono || ''}`}><span>{label}</span><strong title={String(valor)} style={{ '--n': Math.max(4, String(valor).length) } as React.CSSProperties}>{valor}</strong></div>;
}

export function Team() {
  const [per, setPer] = useState(usePeriodoInicial());
  const [d, setD] = useState<Resp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = (v = per, silencio = false) => {
    if (!silencio) setD(null);
    setError(null);
    const qs = periodoQS(v, 'todo');
    return api<Resp>('crm/team' + (qs ? `?${qs}` : '')).then(setD).catch((e) => setError(e.message));
  };
  useEffect(() => { load(per); }, [per]);
  // Es el tablero que se mira mientras el equipo trabaja: lo que llega de Halory (inbound), cobros y citas
  // suben solos. `inbound` es el aviso del webhook de Halory: sin él, una persona que escribe no movía nada aquí.
  useLive(['followup', 'patient', 'inbound'], () => load(per, true));

  const txtPer = periodoTexto(per).toLowerCase();
  // Un servidor anterior no manda `trabaja`: ahí vale lo de antes (sin cuenta = aparte).
  const trabaja = (a: Asesoria) => (a.trabaja !== undefined ? a.trabaja : !a.sin_cuenta);
  const activas = d ? d.asesoria.filter((a) => trabaja(a) && !a.sin_asignar) : [];
  const huerfanas = d ? d.asesoria.filter((a) => !trabaja(a) || a.sin_asignar) : [];
  const idCartera = (a: Asesoria) => (a.sin_asignar ? 'sin' : a.asesora_id);
  const huerfIds = huerfanas.map(idCartera).filter((x): x is string => !!x);
  const huerfTotal = huerfanas.reduce((n, a) => n + a.cartera.asignados, 0);
  const [detalle, setDetalle] = useState<{ titulo: string; subtitulo?: string; asesoras: string[] } | null>(null);
  const [bajando, setBajando] = useState<string | null>(null);
  const bajar = async (clave: string, titulo: string, asesoras: string[]) => {
    setBajando(clave);
    try { await bajarCartera(titulo, asesoras); }
    catch (e) { await avisar({ titulo: 'No se pudo bajar el Excel', mensaje: textoDeError(e), tipo: 'error' }); }
    finally { setBajando(null); }
  };

  return (
    <div>
      <style>{CSS}</style>
      <TopBar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '18px 16px 48px' }}>
        <h1 style={{ margin: '4px 2px 4px', fontSize: 24, fontWeight: 300, color: 'var(--gold-dark)' }}>Equipo</h1>
        <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>Qué hizo cada persona del equipo en las fechas que elijas: seguimiento y ventas de asesoría, lo que realizó el equipo médico y lo que cobró recepción.</p>
        <PeriodPicker value={per} onChange={setPer} />

        {error && <div className="card" style={{ padding: 16, marginTop: 12, color: 'var(--warning)', background: 'var(--warning-bg)' }}>{error}</div>}
        {!d && !error && <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}

        {d && (<>
          <h2 className="eq-h2">Ventas / asesoría</h2>
          <p className="eq-nota">
            Todo sale de Halory y de lo que Halory escribe en la base: conversaciones y chats esperando respuesta (Halory, al momento),
            citas que agendó cada quien o la IA, y lo que compró su cartera (recepción y la tienda de la app) en {txtPer}.
            La cartera es a quién atiende en Halory hoy (Halory avisa cada cambio al momento). Cuenta todo el equipo de Halory,
            tenga o no cuenta en DTB HUB.
          </p>
          {d.halory && d.halory.equipo && !d.halory.equipo.fuente && (
            <div className="card" style={{ padding: '10px 14px', marginBottom: 10, fontSize: 13, color: 'var(--ink-soft)' }} data-halory-equipo-sin>
              Todavía no se sabe quién está hoy en el equipo de Halory (no ha contestado y el reloj no lo ha guardado): por ahora sólo
              se distingue a quien tiene cuenta en el HUB.
            </div>
          )}
          {d.halory && !d.halory.ok && (
            <div className="card" style={{ padding: '10px 14px', marginBottom: 10, background: 'var(--warning-bg)', color: 'var(--warning)', fontSize: 13, border: 'none' }} data-halory-motivo>
              No se pudo preguntar a Halory: {d.halory.motivo} Las conversaciones salen en «—» hasta que se resuelva; lo demás (citas, dinero, cartera) sí está al día.
            </div>
          )}
          {activas.length === 0 && <div className="card" style={{ padding: 14 }}><span className="muted" style={{ fontSize: 13 }}>No hay cuentas de asesoría activas. Se dan de alta en Administración → Cuentas.</span></div>}
          {/* 0.46.0: la comparación de un vistazo (lo que compró la cartera de cada quien, y cuántas de sus citas llegaron). */}
          {activas.length > 1 && (
            <div className="card" style={{ padding: '14px 16px', marginBottom: 12 }} data-eq-comparar>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--gold-dark)' }}>¿Cómo va cada quien?</h3>
              <div className="muted" style={{ fontSize: 12, margin: '2px 0 10px' }}>Lo que compró su cartera {enPeriodo(per)}, y de sus citas cuántas llegaron. Toca un nombre para ver su cartera.</div>
              <Barras datos="eq-comparar"
                filas={[...activas].sort((a, b) => b.periodo.ventas - a.periodo.ventas).map((a) => ({ clave: a.asesora_id || a.nombre, nombre: a.nombre, n: a.periodo.ventas,
                  extra: `· ${entero(a.periodo.citas)} ${a.periodo.citas === 1 ? 'cita' : 'citas'}${a.periodo.citas ? `, ${porcentaje(a.periodo.asistieron, a.periodo.citas)} llegaron` : ''}` }))}
                formato={(f, tot) => `${dinero(f.n)} · ${porcentaje(f.n, tot)}`}
                onAbrir={(k) => { const a = activas.find((x) => (x.asesora_id || x.nombre) === k); if (a?.asesora_id && a.cartera.asignados > 0) setDetalle({ titulo: `Cartera de ${a.nombre}`, asesoras: [a.asesora_id] }); }} />
            </div>
          )}
          <div className="eq-grid" data-eq="asesoria">
            {activas.map((a) => (
              <div key={a.asesora_id || a.user_id || a.nombre} className="card eq-card" data-persona={a.nombre}>
                <div className="eq-cab">
                  <span style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap', minWidth: 0 }}>
                    <b>{a.nombre}</b>
                    {a.cuenta_hub === false && <span className="eq-pill gris" title="Atiende en Halory. Sus números cuentan igual; la cuenta del HUB sólo sirve para entrar aquí." data-solo-halory>sólo en Halory</span>}
                  </span>
                  {a.cartera.asignados > 0 && a.asesora_id
                    ? <button className="eq-ver" data-ver-cartera onClick={() => setDetalle({ titulo: `Cartera de ${a.nombre}`, asesoras: [a.asesora_id!] })}>{a.cartera.asignados} en su cartera</button>
                    : <span className="muted" style={{ fontSize: 12 }}>{a.cartera.asignados} en su cartera</span>}
                </div>
                <div className="eq-kpis">
                  <K label="Conversaciones" valor={a.periodo.conversaciones ?? '—'} />
                  <K label="Esperan respuesta" valor={a.periodo.esperando ?? '—'} tono={a.periodo.esperando ? 'ambar' : undefined} />
                  <K label="Citas agendadas" valor={a.periodo.citas} tono="oro" />
                  <K label="Asistieron" valor={a.periodo.asistieron} />
                  <K label="Vendido a su cartera" valor={money(a.periodo.ventas)} tono="oro" />
                  <K label="Tickets" valor={a.periodo.tickets} />
                </div>
                <div className="eq-cartera">
                  {a.periodo.citas_ia > 0 && <>{a.periodo.citas_ia} de sus citas las agendó la IA de Halory · </>}
                  {a.periodo.escribieron > 0 && <><b>{a.periodo.escribieron}</b> de su cartera escribieron · </>}
                  {a.periodo.nuevos > 0 && <><b>{a.periodo.nuevos}</b> nuevos asignados · </>}
                  Hoy: <b>{a.cartera.pacientes}</b> pacientes · <b>{a.cartera.leads}</b> leads
                  {a.cartera.quejas > 0 && <> · <b style={{ color: 'var(--warning)' }}>{a.cartera.quejas} queja{a.cartera.quejas === 1 ? '' : 's'} activa{a.cartera.quejas === 1 ? '' : 's'}</b></>}
                  {!!a.periodo.quejas_chat && <> · <b style={{ color: 'var(--warning)' }}>{a.periodo.quejas_chat} chat{a.periodo.quejas_chat === 1 ? '' : 's'} de queja</b></>}
                </div>
              </div>
            ))}
          </div>
          {huerfanas.length > 0 && (
            <div className="card" style={{ padding: '12px 16px', marginTop: 12 }} data-eq="sin-cuenta">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <b style={{ fontSize: 14 }}>Cartera que no trabaja nadie</b>
                {huerfTotal > 0 && (
                  <button className="btn-ghost" style={{ padding: '6px 14px', fontSize: 12.5 }} disabled={bajando === 'todo'} data-bajar-todo
                    onClick={() => bajar('todo', 'Cartera que no trabaja nadie', huerfIds)}>
                    {bajando === 'todo' ? 'Preparando…' : `Bajar todo a Excel (${huerfTotal.toLocaleString('es-MX')})`}
                  </button>
                )}
              </div>
              <p className="muted" style={{ fontSize: 12.5, margin: '4px 0 8px' }}>
                Expedientes a nombre de alguien que hoy no está en el equipo de Halory (casi siempre del Excel del CRM) o de nadie:
                nadie les da seguimiento. Ábrelos o bájalos a Excel para repartirlos; en cuanto alguien los atienda en Halory, pasan a su cartera solos.
              </p>
              <div className="eq-huerf">
                {huerfanas.map((a) => {
                  const id = idCartera(a);
                  const titulo = a.sin_asignar ? 'Sin asignar' : `Cartera de ${a.nombre}`;
                  // Una cuenta del HUB que no está en Halory tampoco atiende: se dice así, no "ya no está".
                  // Sin saber quién está en Halory (no contestó y el reloj todavía no lo guarda) sólo se sabe que no tiene cuenta.
                  const porque = a.sin_asignar ? 'sin asignar' : !d.halory?.equipo?.fuente ? 'sin cuenta activa'
                    : a.cuenta_hub ? 'no está en Halory' : 'ya no está en Halory';
                  return (
                    <div key={a.asesora_id || 'sin'} className="eq-huerf-fila" data-huerfana={a.nombre}>
                      <b>{a.nombre}</b><span className={`eq-pill ${a.sin_asignar ? 'gris' : ''}`} data-porque
                        title={a.cuenta_hub ? 'Tiene cuenta en DTB HUB, pero hoy no está en el equipo de Halory: nadie atiende su cartera.' : undefined}>{porque}</span>
                      <span className="muted">{a.cartera.asignados.toLocaleString('es-MX')} expediente{a.cartera.asignados === 1 ? '' : 's'} · {a.cartera.pacientes} paciente{a.cartera.pacientes === 1 ? '' : 's'} · {a.cartera.leads} lead{a.cartera.leads === 1 ? '' : 's'}{a.periodo.ventas ? ` · su cartera compró ${money(a.periodo.ventas)}` : ''}{a.sin_asignar && a.periodo.esperando ? ` · ${a.periodo.esperando} chat${a.periodo.esperando === 1 ? '' : 's'} sin asignar esperando respuesta` : ''}</span>
                      {id && a.cartera.asignados > 0 && (
                        <span className="acc">
                          <button className="eq-ver" data-ver-lista onClick={() => setDetalle({ titulo, subtitulo: a.sin_asignar ? 'sin nadie asignado' : porque, asesoras: [id] })}>Ver lista</button>
                          <button className="eq-ver" data-bajar disabled={bajando === id} onClick={() => bajar(id, titulo, [id])}>{bajando === id ? 'Preparando…' : 'Excel'}</button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <h2 className="eq-h2">Equipo médico y cosmetología</h2>
          <p className="eq-nota">Lo que realizó cada quien en {txtPer}: lo que recepción marca como "realizó" al cobrar. Su comisión está en <Link to="/comisiones">Comisiones</Link>.</p>
          <div className="eq-grid" data-eq="clinico">
            {d.clinico.length === 0 && <div className="card" style={{ padding: 14 }}><span className="muted" style={{ fontSize: 13 }}>Sin cuentas del equipo médico ni de cosmetología.</span></div>}
            {d.clinico.map((c) => (
              <div key={c.user_id} className="card eq-card" data-persona={c.nombre}>
                <div className="eq-cab"><b>{c.nombre}</b><span className="muted" style={{ fontSize: 12 }}>{ROL_ETIQUETA[c.rol || ''] || c.rol || ''}{!c.activo ? ' · cuenta inactiva' : ''}</span></div>
                <div className="eq-kpis">
                  <K label="Procedimientos" valor={c.procedimientos} />
                  <K label="Cobrado" valor={money(c.cobrado)} tono="oro" />
                  <K label="Pacientes" valor={c.pacientes} />
                </div>
                {c.farmacia.lineas > 0 && <div className="eq-cartera">Farmacia que vendió: <b>{money(c.farmacia.cobrado)}</b> ({c.farmacia.lineas} producto{c.farmacia.lineas === 1 ? '' : 's'})</div>}
                {c.procedimientos === 0 && <div className="eq-cartera">Nada registrado a su nombre en estas fechas. Si sí atendió, recepción no marcó quién realizó al cobrar.</div>}
              </div>
            ))}
          </div>

          <h2 className="eq-h2">Recepción</h2>
          <p className="eq-nota">Lo que cobró cada quien en el punto de venta en {txtPer}.</p>
          <div className="eq-grid" data-eq="recepcion">
            {d.recepcion.length === 0 && <div className="card" style={{ padding: 14 }}><span className="muted" style={{ fontSize: 13 }}>Sin cuentas de recepción ni cobros en estas fechas.</span></div>}
            {d.recepcion.map((r) => (
              <div key={r.user_id} className="card eq-card" data-persona={r.nombre}>
                <div className="eq-cab"><b>{r.nombre}</b><span className="muted" style={{ fontSize: 12 }}>{ROL_ETIQUETA[r.rol || ''] || r.rol || ''}{!r.activo ? ' · cuenta inactiva' : ''}</span></div>
                <div className="eq-kpis">
                  <K label="Tickets" valor={r.tickets} />
                  <K label="Cobrado" valor={money(r.cobrado)} tono="oro" />
                  <K label="Canceladas" valor={r.canceladas} tono={r.canceladas ? 'ambar' : undefined} />
                </div>
                {r.farmacia.lineas > 0 && <div className="eq-cartera">Farmacia que vendió: <b>{money(r.farmacia.cobrado)}</b> ({r.farmacia.lineas} producto{r.farmacia.lineas === 1 ? '' : 's'})</div>}
              </div>
            ))}
          </div>
        </>)}
      </div>
      {detalle && <CarteraDetalle titulo={detalle.titulo} subtitulo={detalle.subtitulo} asesoras={detalle.asesoras} onClose={() => setDetalle(null)} />}
    </div>
  );
}
